import { Column, ColumnDef, Row } from "@tanstack/react-table";
import React, { useCallback, useMemo } from "react";
import { Button } from "../../button";
import { ArrowUpDown } from "lucide-react";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import { cn } from "../../../utils/cn";
import {
  Device,
  DEVICES_APPROVE_REQUEST_DETAILS_TABLE,
  STATUS_IDS,
  USER_ROLES_IDS,
} from "../../../constants/common";
import { useLocation } from "react-router-dom";
import { APP_ROUTES } from "../../../routes";
import useAuthStore from "../../../store/Auth";
import ThreeStateToggle from "../../toggle/ThreeStateToggle";
import { Controller, useForm } from "react-hook-form";
import TextInput from "../../inputs/TextInput";

interface DeviceApprovalDetailsTableProps {
  devices: any;
  type: DEVICES_APPROVE_REQUEST_DETAILS_TABLE;
  setDevices?: React.Dispatch<React.SetStateAction<Device[] | undefined>>;
}

const DeviceApprovalDetailsTable = ({
  devices,
  type,
  setDevices,
}: DeviceApprovalDetailsTableProps) => {
  const { t } = useTranslation();

  const user = useAuthStore((state) => state.user);

  const { control } = useForm();

  const params = useLocation();

  const handleCommentChange = useCallback(
    (deviceId: number, comment: string) => {
      setDevices?.((prev?: Device[]) =>
        prev?.map((d: Device) =>
          d.appointmentDeviceId === deviceId ? { ...d, comment } : d
        )
      );
    },
    [setDevices]
  );

  const threeStatesToggle = useMemo(
    () => [
      {
        id: 1,
        title: t("reqInfo"),
        value: STATUS_IDS.REQUEST_INFO,
      },
      {
        id: 2,
        title: t("reqInfoSelect"),
        value: STATUS_IDS.SELECT,
      },

      {
        id: 3,
        title: t("reqInfoApprove"),
        value: STATUS_IDS.APPROVED,
      },
    ],
    [t]
  );

  const columns: ColumnDef<Device>[] = useMemo(
    () =>
      [
        {
          accessorKey: "select",
          header: () => <></>,
          cell: ({ row }: { row: Row<Device> }) => {
            const handleToggle = (val: string) => {
              setDevices?.((prev: any) =>
                prev.map((device: any) =>
                  device.appointmentDeviceId ===
                  row.original.appointmentDeviceId
                    ? { ...device, status: Number(val) }
                    : device
                )
              );
            };

            const deviceId = row.original.appointmentDeviceId;

            return (
              <div className="flex flex-col gap-2">
                <ThreeStateToggle
                  states={threeStatesToggle}
                  value={
                    row.original.currentUserDeviceStatus
                      ? row.original.currentUserDeviceStatus ===
                        STATUS_IDS.APPROVED
                        ? row.original.currentUserDeviceStatus
                        : row.original.status || STATUS_IDS.SELECT
                      : STATUS_IDS.SELECT
                  }
                  onChange={handleToggle}
                  disabled={
                    row.original.currentUserDeviceStatus === STATUS_IDS.APPROVED
                  }
                />

                {type ===
                  DEVICES_APPROVE_REQUEST_DETAILS_TABLE.APPROVE_DETAILS_TABLE ||
                row.original.status === STATUS_IDS.APPROVED ||
                row.original.currentUserDeviceStatus === STATUS_IDS.APPROVED ? (
                  <></>
                ) : (
                  <Controller
                    name={`comments.${deviceId}`}
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        {...field}
                        label={t("comment")}
                        labelStyle="text-gray-600"
                        value={field.value || ""}
                        onChange={(e) => {
                          field.onChange(e);
                          handleCommentChange?.(deviceId, e.target.value);
                        }}
                      />
                    )}
                  />
                )}
              </div>
            );
          },
        },
        {
          accessorKey: "deviceType",
          header: ({ column }: { column: Column<Device> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("deviceType")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<Device> }) => {
            return (
              <span>
                {row.original.deviceType !== 2250
                  ? row.original.deviceType
                  : `${
                      row.original.deviceTypeOther ||
                      row.original.deviceTypeOthersValue
                    } (${t("other")})`}
              </span>
            );
          },
        },
        {
          accessorKey: "deviceModel",
          header: ({ column }: { column: Column<Device> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("deviceModel")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<Device> }) => {
            return <span>{row.original.deviceModel}</span>;
          },
        },
        {
          accessorKey: "serialNumber",
          header: ({ column }: { column: Column<Device> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("serialNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<Device> }) => {
            return <span>{row.original.serialNumber}</span>;
          },
        },
        {
          accessorKey: "purpose",
          header: ({ column }: { column: Column<Device> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("devicePurpose")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          // cell: ({ row }: { row: Row<Device> }) => {
          //   return (
          //     <span>
          //       {row.original.devicePurpose !== 2254
          //         ? row.original.devicePurpose
          //         : `${
          //             row.original.devicePurposeOther ||
          //             row.original.devicePurposeOthersValue
          //           } (${t("other")})`}
          //     </span>
          //   );
          // },
        },
        {
          accessorKey: "currentUserDeviceStatus",
          header: ({ column }: { column: Column<Device> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {user?.n_RoleID === USER_ROLES_IDS.STAFF
                ? t("staffApprovalStatus")
                : user?.n_RoleID === USER_ROLES_IDS.SECURITY
                ? t("securityApprovalStatus")
                : user?.n_RoleID === USER_ROLES_IDS.CYBER_SECURITY
                ? t("cyberSecurityApprovalStatus")
                : t("securityManagerApprovalStatus")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<Device> }) => {
            if (!row.original.appointmentDeviceId) return <></>;

            return (
              <div
                className={cn(
                  "text-center rounded-lg py-0.5 max-w-[100px]",
                  !row.original.currentUserDeviceStatus
                    ? row.original.approvalStatus === STATUS_IDS.APPROVED
                      ? "bg-green-600"
                      : row.original.approvalStatus === STATUS_IDS.PENDING
                      ? "bg-yellow-500"
                      : "bg-red-500"
                    : row.original.currentUserDeviceStatus ===
                      STATUS_IDS.APPROVED
                    ? "bg-green-600"
                    : row.original.currentUserDeviceStatus ===
                      STATUS_IDS.PENDING
                    ? "bg-yellow-500"
                    : "bg-red-500"
                )}
              >
                <p className="text-sm text-white whitespace-normal">
                  {!row.original.currentUserDeviceStatus
                    ? row.original.approvalStatus === STATUS_IDS.APPROVED
                      ? t("approved")
                      : row.original.approvalStatus === STATUS_IDS.PENDING
                      ? t("pending")
                      : t("rejected")
                    : row.original.currentUserDeviceStatus ===
                      STATUS_IDS.APPROVED
                    ? t("approved")
                    : row.original.currentUserDeviceStatus ===
                      STATUS_IDS.PENDING
                    ? t("pending")
                    : t("rejected")}
                </p>
              </div>
            );
          },
        },
        {
          accessorKey: "approvalStatus",
          header: ({ column }: { column: Column<Device> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("approvalStatus")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<Device> }) => {
            if (!row.original.appointmentDeviceId) return <></>;

            return row.original.approvalStatus ? (
              <div
                className={cn(
                  "text-center rounded-lg py-0.5 max-w-[100px]",
                  row.original.approvalStatus === STATUS_IDS.APPROVED
                    ? "bg-green-600"
                    : row.original.approvalStatus === STATUS_IDS.PENDING
                    ? "bg-yellow-500"
                    : "bg-red-500"
                )}
              >
                <p className="text-sm text-white whitespace-normal">
                  {row.original.approvalStatus === STATUS_IDS.APPROVED
                    ? t("approved")
                    : row.original.approvalStatus === STATUS_IDS.PENDING
                    ? t("pending")
                    : t("rejected")}
                </p>
              </div>
            ) : (
              <></>
            );
          },
        },
        // {
        //   accessorKey: "comment",
        //   header: ({ column }: { column: Column<Device> }) => (
        //     <Button
        //       className="flex w-full justify-between text-md min-w-72"
        //       variant="ghost"
        //       onClick={() =>
        //         column.toggleSorting(column.getIsSorted() === "asc")
        //       }
        //     >
        //       {t("comment")}
        //       <ArrowUpDown className="ml-2 h-4 w-4" />
        //     </Button>
        //   ),
        //   cell: ({ row }: { row: Row<Device> }) => {
        //     if (
        //       row.original.status === STATUS_IDS.APPROVED ||
        //       row.original.currentUserDeviceStatus === STATUS_IDS.APPROVED
        //     )
        //       return;

        //     const deviceId = row.original.appointmentDeviceId;
        //     return (
        //       <Controller
        //         name={`comments.${deviceId}`}
        //         control={control}
        //         render={({ field }) => (
        //           <TextInput
        //             {...field}
        //             value={field.value || ""}
        //             onChange={(e) => {
        //               field.onChange(e);
        //               handleCommentChange?.(deviceId, e.target.value);
        //             }}
        //           />
        //         )}
        //       />
        //     );
        //   },
        // },
      ].filter((column) => {
        let excludeKeys = [];

        if (
          params.pathname === APP_ROUTES.INDIVIDUAL_VISIT_REQUEST ||
          params.pathname === APP_ROUTES.GROUP_VISIT_REQUEST ||
          params.pathname === APP_ROUTES.VIP_VISIT_REQUEST
        ) {
          excludeKeys.push("approvalStatus", "currentUserDeviceStatus");
        }

        const isUpdateRequest = params.pathname === APP_ROUTES.UPDATE_REQUEST;
        const isStaffOrSecurity =
          user?.n_RoleID === USER_ROLES_IDS.STAFF ||
          user?.n_RoleID === USER_ROLES_IDS.SECURITY;

        if (isUpdateRequest) {
          if (isStaffOrSecurity) {
            excludeKeys.push("currentUserDeviceStatus");
          }
        }

        if (
          type === DEVICES_APPROVE_REQUEST_DETAILS_TABLE.APPROVE_DETAILS_TABLE
        ) {
          excludeKeys.push("select");
        }

        return !excludeKeys.includes(column.accessorKey);
      }),
    [
      control,
      handleCommentChange,
      params.pathname,
      setDevices,
      t,
      threeStatesToggle,
      type,
      user?.n_RoleID,
    ]
  );

  return (
    <div className="flex flex-col w-full">
      <TableWrapper
        data={devices}
        columns={columns}
        isPagination={false}
        isColumnChooser={false}
      />
    </div>
  );
};

export default React.memo(DeviceApprovalDetailsTable);
